import { useCallback, useEffect, useState } from 'react';
import type { ChatDraft, StepKey } from './chatSteps';
import { createEmptyDraft, isStepKey } from './chatSteps';

interface PersistedState {
  step: StepKey;
  draft: Omit<ChatDraft, 'images'>; // File[] không serialize được — ảnh luôn mất khi reload, user chọn lại.
}

function storageKey(userId: string): string {
  return `webqn_chatbot_draft_${userId}`;
}

/**
 * Giữ { step, draft } trong localStorage theo userId để user reload trang
 * giữa chừng vẫn tiếp tục được (trừ ảnh đã chọn — File không serialize qua
 * JSON, user chọn lại ở bước ASK_IMAGES nếu cần).
 */
export function useChatDraft(userId: string | null) {
  const [step, setStep] = useState<StepKey>('SELECT_SERVICE');
  const [draft, setDraft] = useState<ChatDraft>(createEmptyDraft());

  useEffect(() => {
    if (!userId) return;
    try {
      const raw = localStorage.getItem(storageKey(userId));
      if (!raw) return;
      const parsed = JSON.parse(raw) as PersistedState;
      // parsed.step có thể là giá trị StepKey cũ/sai (đổi tên StepKey, dữ liệu
      // bị sửa tay trong devtools...) -> fallback về bước đầu để tránh render
      // trống không có đường thoát.
      setStep(isStepKey(parsed.step) ? parsed.step : 'SELECT_SERVICE');
      setDraft({ ...createEmptyDraft(), ...parsed.draft, images: [] });
    } catch {
      // localStorage hỏng/không parse được -> bắt đầu lại từ đầu, không throw.
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const { images, ...rest } = draft;
    const toSave: PersistedState = { step, draft: rest };
    localStorage.setItem(storageKey(userId), JSON.stringify(toSave));
  }, [userId, step, draft]);

  const reset = useCallback(() => {
    setStep('SELECT_SERVICE');
    setDraft(createEmptyDraft());
    if (userId) localStorage.removeItem(storageKey(userId));
  }, [userId]);

  return { step, setStep, draft, setDraft, reset };
}
